/**
 * AB Featured Product Section Logic
 * Integrates with Shopify's native cart system (cart-drawer or cart-notification)
 */

class ABFeaturedProduct {
  constructor(section) {
    // Accept either a section element or a section ID string
    if (typeof section === 'string') {
      this.section = document.getElementById(section);
    } else {
      this.section = section;
    }
    if (!this.section) return;

    this.qtyInput = this.section.querySelector('.ab-quantity-selector__input');
    this.atcBtn = this.section.querySelector('.ab-featured-product__btn--atc');

    this.init();
  }

  init() {
    if (this.atcBtn) {
      this.atcBtn.addEventListener('click', (e) => {
        e.preventDefault();
        this.addToCart();
      });
    }
  }

  async addToCart() {
    const variantId = this.atcBtn.dataset.variantId;
    const quantity = this.qtyInput ? (parseInt(this.qtyInput.value) || 1) : 1;

    if (!variantId) {
      console.warn('[AB Featured Product] No variant ID found on button.');
      return;
    }

    // Capture original text BEFORE adding loading class
    // (innerText returns '' when visibility:hidden is applied by .loading CSS)
    const btnText = this.atcBtn.querySelector('.ab-btn-text');
    const originalText = btnText ? (btnText.textContent.trim() || 'Add to Cart') : 'Add to Cart';

    // Set loading state
    this.atcBtn.classList.add('loading');
    this.atcBtn.setAttribute('aria-disabled', 'true');

    // Find native cart component (drawer takes priority over notification popup)
    const cartComponent = document.querySelector('cart-drawer') || document.querySelector('cart-notification');

    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', quantity);

    // Request section rendering data if cart component supports it
    if (cartComponent && typeof cartComponent.getSectionsToRender === 'function') {
      try {
        const sectionIds = cartComponent.getSectionsToRender().map((s) => s.id);
        formData.append('sections', sectionIds.join(','));
        formData.append('sections_url', window.location.pathname);
      } catch (e) {
        // getSectionsToRender failed – continue without section rendering
      }
    }

    try {
      const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
          'Accept': 'application/javascript',
        },
        body: formData,
      });

      const data = await response.json();

      if (!response.ok) {
        const errorMsg = data.description || data.message || 'Could not add item to cart.';
        alert(errorMsg);
        return;
      }

      // === SUCCESS ===

      // 1. Use theme's pubsub if available
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'ab-featured-product',
          productVariantId: variantId,
          cartData: data,
        });
      }

      // 2. Render the cart component with new section HTML
      if (cartComponent && typeof cartComponent.renderContents === 'function') {
        try {
          cartComponent.renderContents(data);
        } catch (e) {
          // renderContents failed – fall back to manual count refresh
          this.refreshCartCount();
        }
      } else {
        this.refreshCartCount();
      }

      // 3. Show success feedback — text is already captured correctly above
      if (btnText) {
        btnText.textContent = 'Added!';
        setTimeout(() => {
          btnText.textContent = originalText;
        }, 2000);
      }

    } catch (error) {
      console.error('[AB Featured Product] ATC Error:', error);
    } finally {
      // Always clean up loading state + aria-disabled
      this.atcBtn.classList.remove('loading');
      this.atcBtn.removeAttribute('aria-disabled');
    }
  }

  async refreshCartCount() {
    try {
      const response = await fetch(`${window.Shopify.routes.root}cart.js`);
      const cart = await response.json();
      document.querySelectorAll('.cart-count-bubble').forEach((bubble) => {
        const countSpan = bubble.querySelector('span[aria-hidden="true"]');
        if (countSpan) countSpan.innerText = cart.item_count;
        bubble.classList.remove('hidden');
      });
    } catch (e) {
      console.error('[AB Featured Product] Failed to refresh cart count:', e);
    }
  }
}

// ── Initialize ────────────────────────────────────────────────────────────────
// Use a more reliable selector: find every section wrapper directly.
function initABFeaturedProducts() {
  document.querySelectorAll('.ab-featured-product[id]').forEach((el) => {
    new ABFeaturedProduct(el);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initABFeaturedProducts);
} else {
  // DOM already ready (script loaded with defer but DOM was parsed)
  initABFeaturedProducts();
}

// Re-initialize when the Shopify Theme Editor reloads a section
document.addEventListener('shopify:section:load', (event) => {
  const sectionEl = event.target.querySelector('.ab-featured-product[id]');
  if (sectionEl) {
    new ABFeaturedProduct(sectionEl);
  }
});
